import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense } from "react";
import { ArrowRight } from "lucide-react";
import { PageHeader } from "./fordon";
import { FyndanuGrid, FyndanuGridSkeleton, fyndanuListingsQuery } from "@/components/FyndanuGrid";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { FiNav, FiSwedishLink, hreflangPair } from "@/components/FiNav";
import { fiVaunutFaq, fiVaunutSections } from "@/data/fi-content";

const TITLE = "Asuntovaunut myynnissä Ruotsissa – Kalix, lähellä Tornio";
const DESC =
  "Käytetyt asuntovaunut Kalixissa. Kosteusmitattu ja kaasutarkastettu jokainen vaunu. Palvelemme suomeksi ja autamme tuonnissa Suomeen.";
const URL = "https://www.akhusbilfritid.se/fi/asuntovaunut";

export const Route = createFileRoute("/fi/asuntovaunut")({
  codeSplitGroupings: [["loader", "component"]],
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(fyndanuListingsQuery);
    return { faqLd: faqSchema(fiVaunutFaq) };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "fi_FI" },
      { property: "og:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
      { property: "og:image:alt", content: "Asuntovaunut myynnissä Kalixissa" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://www.akhusbilfritid.se/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: URL }, ...hreflangPair("/fi/asuntovaunut", "/husvagnar-till-salu")],
    scripts: loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : [],
  }),
  component: FiVaunutPage,
});

function FiVaunutPage() {
  return (
    <>
      <PageHeader
        label="Asuntovaunut · Kosteusmitattu ja kaasutarkastettu"
        title={
          <>
            Asuntovaunut <span className="font-italic-display text-accent">myynnissä</span>.
          </>
        }
        sub="Vaunu antaa samalla rahalla enemmän asuintilaa ja jättää vetoauton vapaaksi leiripaikalla. Alta löydät varastomme — kysy rohkeasti lisää suomeksi."
      />

      <FiNav />

      <section className="container-edge py-12">
        <p className="eyebrow">Varasto</p>
        <Suspense fallback={<FyndanuGridSkeleton />}>
          <FyndanuGrid />
        </Suspense>
        <div className="mt-8">
          <Link
            to="/fi/yhteystiedot"
            className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
          >
            Kysy vaunusta suomeksi <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      <HubEditorial
        eyebrow="Ostajan opas"
        sections={fiVaunutSections}
        faq={fiVaunutFaq}
        faqTitle="Usein kysytyt kysymykset"
      />

      <FiSwedishLink to="/husvagnar-till-salu" label="Husvagnar till salu (svenska)" />
    </>
  );
}
